import { useRef, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import Spinner from '../spinner/Spinner';
import { axiosInstancev2 } from '../../utils/Axios/axiosInterceptorv2';
import { fileToBase64 } from '../../helpers/convertToBase64';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function CloseTicketEmail({
  ticketId,
  recipientName,
  recipientEmail,
  defaultSubject = '',
  onCancel,
  onClosed,
}) {
  const fileInputRef = useRef(null);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [messageError, setMessageError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addFiles = (fileList) => {
    const selected = Array.from(fileList || []);
    if (!selected.length) return;

    const oversized = selected.filter((file) => file.size > MAX_FILE_BYTES);
    if (oversized.length) {
      toast.error('Each attachment must be 10MB or smaller');
    }

    const accepted = selected.filter((file) => file.size <= MAX_FILE_BYTES);
    setFiles((current) => {
      const existing = new Set(
        current.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
      );
      const next = accepted.filter((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        return !existing.has(key);
      });
      return [...current, ...next];
    });
  };

  const removeFile = (index) => {
    setFiles((current) =>
      current.filter((file, fileIndex) => fileIndex !== index)
    );
  };

  const buildAttachments = async () => {
    return Promise.all(
      files.map(async (file) => {
        const dataUrl = await fileToBase64(file);
        const contentBase64 = String(dataUrl).includes(',')
          ? String(dataUrl).split(',')[1]
          : String(dataUrl);

        return {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          content_base64: contentBase64,
        };
      })
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setMessageError('Message is required');
      return;
    }
    setMessageError('');
    setIsSubmitting(true);

    try {
      const body = { message: trimmedMessage };
      const trimmedSubject = subject.trim();
      if (trimmedSubject) body.subject = trimmedSubject;
      if (files.length) body.attachments = await buildAttachments();

      await axiosInstancev2.put(`support/tickets/${ticketId}/close`, body);
      toast.success('Email sent and ticket closed');
      onClosed();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Please try again';
      toast.error(
        `Email was sent, but the ticket could not be closed. ${apiMessage}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const recipientLabel = [
    recipientName,
    recipientEmail && `<${recipientEmail}>`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <form className="text-gray-700 space-y-4" onSubmit={handleSubmit}>
      <p className="text-sm text-gray-600">
        This email is sent
        {recipientLabel ? (
          <>
            {' '}
            to{' '}
            <span className="font-medium text-gray-800">{recipientLabel}</span>
          </>
        ) : null}{' '}
        before the ticket is closed.
      </p>

      <div className="flex flex-col space-y-1">
        <label htmlFor="close-ticket-subject" className="text-sm">
          Subject <span className="text-gray-400">(optional)</span>
        </label>
        <input
          id="close-ticket-subject"
          type="text"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          placeholder="Subject"
          disabled={isSubmitting}
          className="border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none"
        />
      </div>

      <div className="flex flex-col space-y-1">
        <label htmlFor="close-ticket-message" className="text-sm">
          Message
        </label>
        <textarea
          id="close-ticket-message"
          value={message}
          onChange={(event) => {
            setMessage(event.target.value);
            if (messageError) setMessageError('');
          }}
          rows={6}
          placeholder="Write the email to the student"
          disabled={isSubmitting}
          className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none resize-y ${
            messageError ? 'border-red-600' : ''
          }`}
        />
        {messageError && (
          <span className="text-red-600 text-xs">{messageError}</span>
        )}
      </div>

      <div className="flex flex-col space-y-2">
        <span className="text-sm">Attachments</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            const { files: selectedFiles } = event.target;
            addFiles(selectedFiles);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
        />
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => fileInputRef.current?.click()}
          className="border-[1px] border-[#CBCBCB] px-3 py-2 rounded-lg text-sm flex items-center space-x-2 w-fit hover:bg-gray-50 disabled:opacity-50"
        >
          <AttachFileIcon fontSize="small" />
          <span>Add attachments</span>
        </button>
        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="flex items-center justify-between border border-[#CBCBCB] rounded-lg px-3 py-2 text-sm"
              >
                <span className="truncate pr-3">
                  {file.name}
                  <span className="text-gray-400 ml-2">
                    {formatFileSize(file.size)}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  disabled={isSubmitting}
                  onClick={() => removeFile(index)}
                  className="text-gray-500 hover:text-red-600 disabled:opacity-50"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isSubmitting ? (
        <div className="w-full flex align-middle justify-center items-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex justify-between mt-8">
          <button
            className="border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center"
            type="button"
            onClick={onCancel}
          >
            <CancelIcon fontSize="inherit" />
            <p>Cancel</p>
          </button>
          <button
            className="bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center"
            type="submit"
          >
            <SendIcon fontSize="inherit" />
            <p>Send and close</p>
          </button>
        </div>
      )}
    </form>
  );
}

export default CloseTicketEmail;
